/**
 * profile-ui.ts - User profile management UI
 */

import {
  fetchProfile,
  upsertProfile,
  checkProfileCompleteness,
  getUserRegistrations,
  type UserProfile
} from '../api/profile-api';
import { getUserRegistrations as fetchRegistrations } from '../api/registration-api';
import { checkAuthStatus } from '../auth/auth-api';

/**
 * Initialize the profile page
 */
export async function initProfilePage(): Promise<void> {
  // Check if we're on the profile page
  if (!document.getElementById('profile-page')) {
    return;
  }

  // Check authentication
  const user = await checkAuthStatus();

  if (!user) {
    showAuthRequired();
    return;
  }

  // Load profile data
  await loadProfile();

  // Load registrations
  await loadRegistrations();

  // Set up form handlers
  setupProfileForm();
}

/**
 * Show authentication required message
 */
function showAuthRequired(): void {
  const authRequired = document.getElementById('profile-auth-required');
  const profileContent = document.getElementById('profile-content');

  if (authRequired) authRequired.style.display = 'block';
  if (profileContent) profileContent.style.display = 'none';
}

/**
 * Load and display user profile
 */
async function loadProfile(): Promise<void> {
  try {
    const profile = await fetchProfile();
    const completeness = await checkProfileCompleteness();

    // Show content
    const profileContent = document.getElementById('profile-content');
    if (profileContent) profileContent.style.display = 'block';

    // Populate form if profile exists
    if (profile) {
      populateProfileForm(profile);
    }

    // Show completeness banner if incomplete
    if (!completeness.isComplete) {
      showCompletenessBanner(completeness.completionPercentage);
    }
  } catch (error) {
    console.error('Failed to load profile:', error);
    showError('Failed to load profile. Please try again.');
  }
}

/**
 * Populate the profile form with data
 */
function populateProfileForm(profile: UserProfile): void {
  const form = document.getElementById('profile-form') as HTMLFormElement;
  if (!form) return;

  (document.getElementById('profile-first-name') as HTMLInputElement).value = profile.firstName || '';
  (document.getElementById('profile-last-name') as HTMLInputElement).value = profile.lastName || '';
  (document.getElementById('profile-rating-std') as HTMLInputElement).value = profile.ratingStd?.toString() || '';
  (document.getElementById('profile-rating-rapid') as HTMLInputElement).value = profile.ratingRapid?.toString() || '';
  (document.getElementById('profile-rating-blitz') as HTMLInputElement).value = profile.ratingBlitz?.toString() || '';
  (document.getElementById('profile-title') as HTMLSelectElement).value = profile.title || '';
  // Handle date format - extract YYYY-MM-DD from ISO string or Date
  const birthDateValue = profile.birthDate ? profile.birthDate.substring(0, 10) : '';
  (document.getElementById('profile-birth-date') as HTMLInputElement).value = birthDateValue;
  (document.getElementById('profile-gender') as HTMLSelectElement).value = profile.gender || '';
  (document.getElementById('profile-federation-code') as HTMLInputElement).value = profile.federationCode || '';
  (document.getElementById('profile-federation-id') as HTMLInputElement).value = profile.federationId || '';
  (document.getElementById('profile-fide-id') as HTMLInputElement).value = profile.fideId || '';
  (document.getElementById('profile-club') as HTMLInputElement).value = profile.club || '';
}

/**
 * Show profile completeness banner
 */
function showCompletenessBanner(percentage: number): void {
  const banner = document.getElementById('profile-completeness-banner');
  const percentEl = document.getElementById('profile-completeness-percent');

  if (banner) banner.style.display = 'block';
  if (percentEl) percentEl.textContent = Math.round(percentage).toString();
}

/**
 * Set up profile form handlers
 */
function setupProfileForm(): void {
  const form = document.getElementById('profile-form') as HTMLFormElement;
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    await saveProfile();
  });

  // Set up Fetch FIDE button
  const fetchFideBtn = document.getElementById('fetch-fide-btn');
  if (fetchFideBtn) {
    fetchFideBtn.addEventListener('click', async () => {
      await fetchFromFide();
    });
  }
}

/**
 * Save profile data
 */
async function saveProfile(): Promise<void> {
  const form = document.getElementById('profile-form') as HTMLFormElement;
  if (!form) return;

  const saveBtn = document.getElementById('save-profile-btn') as HTMLButtonElement;
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';
  }

  try {
    const profileData = {
      firstName: (document.getElementById('profile-first-name') as HTMLInputElement).value,
      lastName: (document.getElementById('profile-last-name') as HTMLInputElement).value || '',
      ratingStd: parseInt((document.getElementById('profile-rating-std') as HTMLInputElement).value) || undefined,
      ratingRapid: parseInt((document.getElementById('profile-rating-rapid') as HTMLInputElement).value) || undefined,
      ratingBlitz: parseInt((document.getElementById('profile-rating-blitz') as HTMLInputElement).value) || undefined,
      title: (document.getElementById('profile-title') as HTMLSelectElement).value || undefined,
      birthDate: (document.getElementById('profile-birth-date') as HTMLInputElement).value || undefined,
      gender: (document.getElementById('profile-gender') as HTMLSelectElement).value || undefined,
      federationCode: (document.getElementById('profile-federation-code') as HTMLInputElement).value || undefined,
      federationId: (document.getElementById('profile-federation-id') as HTMLInputElement).value || undefined,
      fideId: (document.getElementById('profile-fide-id') as HTMLInputElement).value || undefined,
      club: (document.getElementById('profile-club') as HTMLInputElement).value || undefined
    };

    await upsertProfile(profileData);

    // Reload to update completeness
    window.location.reload();
  } catch (error) {
    console.error('Failed to save profile:', error);
    showError(error instanceof Error ? error.message : 'Failed to save profile');
  } finally {
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Profile';
    }
  }
}

/**
 * Load user's tournament registrations
 */
async function loadRegistrations(): Promise<void> {
  const loadingEl = document.getElementById('registrations-loading');
  const emptyEl = document.getElementById('registrations-empty');
  const containerEl = document.getElementById('registrations-list-container');

  if (loadingEl) loadingEl.style.display = 'block';

  try {
    const registrations = await fetchRegistrations();

    if (loadingEl) loadingEl.style.display = 'none';

    if (registrations.length === 0) {
      if (emptyEl) emptyEl.style.display = 'block';
      return;
    }

    if (containerEl) {
      containerEl.innerHTML = registrations.map(reg => {
        const tournamentName = reg.tournament?.name || 'Tournament';
        const shareToken = reg.tournament?.shareToken;
        const shareUrl = shareToken ? `/share/${shareToken}` : '#';
        const registeredDate = new Date(reg.createdAt).toLocaleDateString();

        return `
          <div class="registration-card">
            <div class="registration-card-content">
              <h4>
                ${shareToken
                  ? `<a href="${shareUrl}" target="_blank">${tournamentName} <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a>`
                  : tournamentName
                }
              </h4>
              <div class="registration-card-meta">
                <span>${registeredDate}</span>
                ${reg.waitlistPosition ? `<span>Waitlist #${reg.waitlistPosition}</span>` : ''}
              </div>
            </div>
            <span class="status-badge status-${reg.status}">${reg.status}</span>
          </div>
        `;
      }).join('');
    }
  } catch (error) {
    console.error('Failed to load registrations:', error);
    if (loadingEl) loadingEl.style.display = 'none';
  }
}

/**
 * Fetch player data from FIDE database
 */
async function fetchFromFide(): Promise<void> {
  const fideIdInput = document.getElementById('profile-fide-id') as HTMLInputElement;
  const fetchBtn = document.getElementById('fetch-fide-btn') as HTMLButtonElement;

  if (!fideIdInput || !fetchBtn) return;

  const fideId = fideIdInput.value.trim();
  if (!fideId) {
    showError('Please enter a FIDE ID first');
    return;
  }

  // Show loading state
  const originalText = fetchBtn.textContent || '';
  fetchBtn.disabled = true;
  fetchBtn.textContent = '⏳ Fetching...';

  try {
    const response = await fetch(`/api/tournaments/fide-search?query=${encodeURIComponent(fideId)}`, {
      credentials: 'include',
    });
    const html = await response.text();

    // Parse FIDE results
    const parser = new DOMParser();
    const doc = parser.parseFromString(html.replace(/<base[^>]*>/gi, ''), 'text/html');
    const rows = doc.querySelectorAll('tbody tr');

    let found = false;
    for (const row of rows) {
      const cells = row.querySelectorAll('td');
      if (cells.length >= 9) {
        const rowFideId = cells[0]?.textContent?.trim();
        if (rowFideId === fideId) {
          // Found exact match
          const nameCell = cells[1];
          const link = nameCell?.querySelector('a');
          const fullName = link?.textContent?.trim() || '';
          const title = cells[2]?.textContent?.trim();
          const federation = cells[4]?.textContent?.trim();
          const ratingStd = parseInt(cells[5]?.textContent?.trim() || '') || undefined;
          const ratingRapid = parseInt(cells[6]?.textContent?.trim() || '') || undefined;
          const ratingBlitz = parseInt(cells[7]?.textContent?.trim() || '') || undefined;

          // Parse name (FIDE format is "Lastname, Firstname")
          let firstName = '', lastName = '';
          if (fullName.includes(',')) {
            const parts = fullName.split(',').map(p => p.trim());
            lastName = parts[0] || '';
            firstName = parts[1] || '';
          }

          // Populate form fields
          const firstNameInput = document.getElementById('profile-first-name') as HTMLInputElement;
          const lastNameInput = document.getElementById('profile-last-name') as HTMLInputElement;
          const titleInput = document.getElementById('profile-title') as HTMLSelectElement;
          const fedCodeInput = document.getElementById('profile-federation-code') as HTMLInputElement;
          const ratingStdInput = document.getElementById('profile-rating-std') as HTMLInputElement;
          const ratingRapidInput = document.getElementById('profile-rating-rapid') as HTMLInputElement;
          const ratingBlitzInput = document.getElementById('profile-rating-blitz') as HTMLInputElement;

          if (firstNameInput && firstName) firstNameInput.value = firstName;
          if (lastNameInput && lastName) lastNameInput.value = lastName;
          if (titleInput && title) titleInput.value = title;
          if (fedCodeInput && federation) fedCodeInput.value = federation.replace(/\s+/g, ' ').trim();
          if (ratingStdInput && ratingStd) ratingStdInput.value = String(ratingStd);
          if (ratingRapidInput && ratingRapid) ratingRapidInput.value = String(ratingRapid);
          if (ratingBlitzInput && ratingBlitz) ratingBlitzInput.value = String(ratingBlitz);

          found = true;
          break;
        }
      }
    }

    if (!found) {
      showError('Player not found in FIDE database');
    }
  } catch (error) {
    console.error('Error fetching from FIDE:', error);
    showError('Error fetching data from FIDE');
  } finally {
    fetchBtn.disabled = false;
    fetchBtn.textContent = originalText;
  }
}

/**
 * Show error message
 */
function showError(message: string): void {
  // Use existing notification system if available, or create simple alert
  if ((window as any).showNotification) {
    (window as any).showNotification(message, 'error');
  } else {
    alert(message);
  }
}

// Initialize on DOM load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProfilePage);
} else {
  initProfilePage();
}
